import {
  GridColDef,
  GridCellParams,
  GridValidRowModel,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from '@mui/x-data-grid-pro';
import { DataPointWithValue } from '@src/types/DataPoint';
import { PointConfigRef, ValueGenerationFunction } from '@src/types/Widget';
import { getValueGenerationFunction } from '@utils/evaluateValueGenerationFunction';
import { DEFAULT_PRECISION, isValidNumber } from '@utils/index_ts';
import { createFieldIdentifier } from '@src/utils/gridUtils';
import { compareAlphaNum } from '@utils/sortAlphaNum';
import Cell from './Cell';
import CustomDetailPanelToggle from './CustomDetailPanelToggle';
import CommandButtonColumn from './CommandButtonColumn';

export function createStaticColumns(
  alias: (text: string) => string = (text) => text,
): GridColDef[] {
  return [
    {
      field: 'id',
      headerName: alias('Device ID'),
      flex: 1,
      type: 'string',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'name',
      headerName: alias('Device'),
      flex: 1,
      type: 'string',
      align: 'center',
      headerAlign: 'center',
      sortComparator: (v1, v2) => compareAlphaNum(String(v1), String(v2)),
    },
  ];
}

export function createCommandColumn(
  openCmdModal: (deviceId: string) => void,
  disableBtn?: boolean,
  alias: (text: string) => string = (text) => text,
): GridColDef {
  return {
    field: 'cmdBtn',
    headerName: alias('CMD'),
    flex: 0.6,
    sortable: false,
    filterable: false,
    type: 'string',
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <CommandButtonColumn
        deviceId={params.row.id}
        openCmdModal={openCmdModal}
        disabled={disableBtn}
      />
    ),
  };
}

export function createLiveDataColumns(
  pointConfigRefs: PointConfigRef[],
): GridColDef[] {
  const visiblePointConfigRefs = pointConfigRefs.filter(
    (pointConfigRef) => !pointConfigRef.hidden,
  );

  return visiblePointConfigRefs.map((pc): GridColDef => {
    const func = getValueGenerationFunction(
      pc.valueGenerationFunction?.function,
    );
    const fieldIdentifier = createFieldIdentifier(pc);

    return {
      field: fieldIdentifier.fieldName,
      headerName: pc.displayName || pc.pointName,
      flex: 1,
      type: 'string',
      align: 'center',
      cellClassName: 'fractal-datapoint-value-cell',
      headerAlign: 'center',
      ...(func
        ? {
            valueGetter: (
              value: DataPointWithValue,
              row: GridValidRowModel,
            ): DataPointWithValue =>
              ({
                ...value,
                value: func(
                  (
                    pc.valueGenerationFunction as ValueGenerationFunction
                  ).argumentArray.map((pointAlias) => row[pointAlias]?.value),
                )?.toFixed(
                  isValidNumber(pc.decimalPrecision)
                    ? pc.decimalPrecision
                    : DEFAULT_PRECISION,
                ),
              }) as DataPointWithValue,
          }
        : {}),
      renderCell: (
        params: GridCellParams<GridValidRowModel, DataPointWithValue>,
      ) => (params.value ? <Cell dataPoint={params.value} /> : '-'),
    };
  });
}

export function createDetailPanelColumn(): GridColDef {
  return {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    renderCell: (params: GridCellParams) => (
      <CustomDetailPanelToggle id={params.id} value={params.value as boolean} />
    ),
  };
}

export function getColumns(
  pointConfigRefs: PointConfigRef[],
  enableCmdBtnColumn?: boolean,
  disableBtn?: boolean,
  openCmdModal?: (deviceId: string) => void,
  alias: (text: string) => string = (text) => text,
): GridColDef[] {
  const staticCols = createStaticColumns(alias);
  const columns: GridColDef[] = [...staticCols];

  if (enableCmdBtnColumn && openCmdModal !== undefined) {
    columns.push(createCommandColumn(openCmdModal, disableBtn, alias));
  }

  const liveDataCols = createLiveDataColumns(pointConfigRefs);
  columns.push(...liveDataCols);
  columns.push(createDetailPanelColumn());

  return columns;
}
